import { useRef, useEffect, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useColumnLayout } from "@/lib/useColumnLayout";
import SortLabel from "@/components/SortLabel";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

const ROW_H = 38; // altezza fissa riga (celle a riga singola) per la virtualizzazione

// Griglia Contabilità stile Excel, config-driven e VIRTUALIZZATA: vengono
// renderizzate solo le righe visibili a schermo (via @tanstack/react-virtual)
// così anche con migliaia di righe la RAM e la fluidità restano ottime.
export default function ContabilitaGrid({
  columns, rows, draftRowClassName = "bg-slate-50/60 border-b border-slate-100",
  sortKey, sortDir, onSort, containerClassName = "", scrollKey,
}) {
  const { orderedColumns, widths, setWidth, moveColumn } = useColumnLayout("contabilita", columns);
  const dragId = useRef(null);
  const resizing = useRef(null);
  const scrollRef = useRef(null);
  const lastScrollKey = useRef(null);
  const pendingFocus = useRef(null);
  const totalWidth = orderedColumns.reduce((s, c) => s + (widths[c.id] || 160), 0);

  const idToIndex = useMemo(() => {
    const m = {};
    rows.forEach((r, i) => { m[r.id] = i; });
    return m;
  }, [rows]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualItems.length ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length ? totalSize - virtualItems[virtualItems.length - 1].end : 0;

  // All'apertura di una contabilità porta la vista in fondo (ultima riga + bozza).
  useEffect(() => {
    if (rows.length === 0) return;
    if (lastScrollKey.current === scrollKey) return;
    lastScrollKey.current = scrollKey;
    rowVirtualizer.scrollToIndex(rows.length - 1, { align: "end" });
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [scrollKey, rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Se una navigazione da tastiera punta a una riga non ancora renderizzata,
  // dopo lo scroll la mettiamo a fuoco appena compare nel DOM.
  useEffect(() => {
    if (!pendingFocus.current) return;
    const { id, colId } = pendingFocus.current;
    const rowEl = scrollRef.current?.querySelector(`tr[data-rowid="${CSS.escape(id)}"]`);
    if (rowEl && focusColInRow(rowEl, colId)) pendingFocus.current = null;
  });

  const onResizeStart = (e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { colId, startX: e.clientX, startW: widths[colId] || 160 };
    const onMove = (ev) => {
      if (!resizing.current) return;
      setWidth(resizing.current.colId, resizing.current.startW + (ev.clientX - resizing.current.startX));
    };
    const onUp = () => {
      resizing.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const hasFooter = orderedColumns.some((c) => typeof c.footer === "function");

  const focusCell = (td) => {
    if (!td) return false;
    const f = td.querySelector("input, button, textarea, [tabindex]");
    if (!f) return false;
    f.focus();
    if (typeof f.select === "function") { try { f.select(); } catch (_) { /* noop */ } }
    return true;
  };
  const focusColInRow = (rowEl, colId) => {
    if (!rowEl || !colId) return false;
    return focusCell(rowEl.querySelector(`td[data-col="${CSS.escape(colId)}"]`));
  };

  // Naviga alla riga di dati per indice (index >= rows.length => riga bozza),
  // scorrendo se necessario quando la riga è fuori dalla finestra virtualizzata.
  const goToDataRow = (index, colId) => {
    if (index < 0) return false;
    if (index >= rows.length) {
      const draftEl = scrollRef.current?.querySelector('tr[data-rowid="__draft__"]');
      return focusColInRow(draftEl, colId);
    }
    const id = rows[index].id;
    const el = scrollRef.current?.querySelector(`tr[data-rowid="${CSS.escape(id)}"]`);
    if (el) return focusColInRow(el, colId);
    pendingFocus.current = { id, colId };
    rowVirtualizer.scrollToIndex(index, { align: "center" });
    return true;
  };

  const onGridKeyDown = (e) => {
    const key = e.key;
    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter"].includes(key)) return;
    if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
    const el = document.activeElement;
    const td = el && el.closest ? el.closest("td") : null;
    const tr = td ? td.closest("tr") : null;
    if (!td || !tr || !tr.dataset || tr.dataset.rowid === undefined) return;
    const colId = td.dataset.col;
    const editable = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
    const caretAtStart = () => {
      if (!editable || el.type === "number") return true;
      try { return el.selectionStart === 0 && el.selectionEnd === 0; } catch (_) { return true; }
    };
    const caretAtEnd = () => {
      if (!editable || el.type === "number") return true;
      try { return el.selectionStart === (el.value || "").length && el.selectionEnd === (el.value || "").length; } catch (_) { return true; }
    };
    const cells = Array.from(tr.children);
    const colIndex = cells.indexOf(td);
    const goCol = (delta) => {
      for (let i = colIndex + delta; i >= 0 && i < cells.length; i += delta) {
        if (focusCell(cells[i])) return true;
      }
      return false;
    };

    const rowId = tr.dataset.rowid;
    const curIdx = rowId === "__draft__" ? rows.length : idToIndex[rowId];

    if (key === "ArrowDown") {
      e.preventDefault();
      if (curIdx !== undefined) goToDataRow(curIdx + 1, colId);
    } else if (key === "ArrowUp") {
      e.preventDefault();
      if (curIdx !== undefined) goToDataRow(curIdx - 1, colId);
    } else if (key === "Enter") {
      if (editable && curIdx !== undefined && goToDataRow(curIdx + 1, colId)) e.preventDefault();
    } else if (key === "ArrowRight") {
      if (caretAtEnd() && goCol(1)) e.preventDefault();
    } else if (key === "ArrowLeft") {
      if (caretAtStart() && goCol(-1)) e.preventDefault();
    }
  };

  const renderCells = (row) =>
    orderedColumns.map((c) => (
      <td key={c.id} data-col={c.id}
        className={cn("border-r border-slate-100 overflow-hidden", typeof c.cellClassName === "function" ? c.cellClassName(row) : c.cellClassName)}>
        {c.cell ? c.cell(row) : row[c.id]}
      </td>
    ));

  return (
    <div ref={scrollRef} className={cn("overflow-auto", containerClassName)}>
      <table onKeyDown={onGridKeyDown} className="border-collapse font-mono-data text-sm" style={{ width: totalWidth, minWidth: "100%", tableLayout: "fixed" }}>
        <colgroup>
          {orderedColumns.map((c) => <col key={c.id} style={{ width: widths[c.id] || 160 }} />)}
        </colgroup>
        <thead className="sticky top-0 z-20">
          <tr className="bg-slate-100 border-b border-slate-200">
            {orderedColumns.map((c) => (
              <th
                key={c.id}
                data-testid={`col-contabilita-${c.id}`}
                className="relative border-b border-r border-slate-200 select-none"
                draggable={c.reorderable !== false}
                onDragStart={() => { dragId.current = c.id; }}
                onDragOver={(e) => { if (dragId.current && dragId.current !== c.id) e.preventDefault(); }}
                onDrop={() => { if (dragId.current) { moveColumn(dragId.current, c.id); dragId.current = null; } }}
              >
                <div className="flex items-center justify-center gap-1 px-1.5 py-1">
                  {c.reorderable !== false && (
                    <GripVertical data-testid={`col-grip-contabilita-${c.id}`} className="h-3 w-3 text-slate-300 cursor-grab flex-shrink-0" />
                  )}
                  {c.sortField ? (
                    <SortLabel label={c.label} active={sortKey === c.sortField} dir={sortDir}
                      onClick={() => onSort(c.sortField)} testid={`sort-${c.sortField}`} />
                  ) : (
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 truncate">{c.label}</span>
                  )}
                  {c.filter}
                </div>
                {c.resizable !== false && (
                  <span
                    data-testid={`col-resize-contabilita-${c.id}`}
                    onMouseDown={(e) => onResizeStart(e, c.id)}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/50"
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr aria-hidden="true"><td colSpan={orderedColumns.length} style={{ height: paddingTop, padding: 0, border: 0 }} /></tr>
          )}
          {virtualItems.map((vi) => {
            const row = rows[vi.index];
            return (
              <tr key={row.id} data-rowid={row.id} data-testid={`riga-${row.id}`}
                style={{ height: ROW_H }}
                className="hover:bg-slate-50 border-b border-slate-100">
                {renderCells(row)}
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true"><td colSpan={orderedColumns.length} style={{ height: paddingBottom, padding: 0, border: 0 }} /></tr>
          )}
          <tr data-rowid="__draft__" className={draftRowClassName}>
            {orderedColumns.map((c) => (
              <td key={c.id} data-col={c.id} className={cn("border-r border-slate-100", c.draftClassName || "p-1.5")}>
                {c.draft ? c.draft() : null}
              </td>
            ))}
          </tr>
        </tbody>
        {hasFooter && (
          <tfoot>
            <tr className="bg-blue-50 border-t border-slate-200">
              {orderedColumns.map((c) => (
                <td key={c.id} className="px-2 py-3">{c.footer ? c.footer() : null}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
